// hooks/useActionLog.js
import { useCallback } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { generateDeviceId } from '../utils/deviceUtils';
import { getKoreanTimeISO } from '../utils/timeUtils';

// 액션 카테고리 정의
export const ACTION_CATEGORIES = {
    CUSTOMER: '고객관리',
    FEATURE: '기능',
    ONBOARDING: '온보딩',
    SESSION: '세션',
    REPORT: '리포트'
};

// 표준화된 액션 타입 정의
export const ACTION_TYPES = {
    // 고객관리
    CUSTOMER_ADD: '고객_추가',
    CUSTOMER_EDIT: '고객_수정',
    CUSTOMER_DELETE: '고객_삭제',
    CUSTOMER_VIEW: '고객_상세보기',
    CUSTOMER_MOVE: '고객_단계_이동',
    
    // 기능
    MAP_OPEN: '지도_열기',
    AI_ASSISTANT_OPEN: 'AI비서_열기',
    SETTINGS_OPEN: '설정_열기',
    NOTICE_VIEW: '공지사항_보기',
    HISTORY_VIEW: '히스토리_보기',
    HISTORY_ADD: '히스토리_추가',
    HISTORY_EDIT: '히스토리_수정',
    HISTORY_DELETE: '히스토리_삭제',
    REPORT_GENERATE: '리포트_생성',
    CSV_EXPORT: 'CSV_내보내기',
    
    // 온보딩
    ONBOARDING_START: '온보딩_시작',
    ONBOARDING_COMPLETE: '온보딩_완료',
    FIRST_CUSTOMER_ADD: '첫고객_추가',
    FIRST_FEATURE_USE: '기능_첫사용',
    
    // 세션
    SESSION_END: '세션_종료'
};

// 세션 ID 생성/가져오기
const getOrCreateSessionId = () => {
    try {
        let sessionId = sessionStorage.getItem('sessionId');
        if (!sessionId) {
            sessionId = Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
            sessionStorage.setItem('sessionId', sessionId);
        }
        return sessionId;
    } catch (error) {
        // sessionStorage를 사용할 수 없는 경우 임시 세션 ID 생성
        return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
    }
};

export const useActionLog = () => {
    // 사용자 액션 로그 전송
    const sendActionLog = useCallback(async (action, details = {}, category = null) => {
        try {
            // 액션 타입에서 자동으로 카테고리 추론
            if (!category) {
                if (action.startsWith('고객_')) {
                    category = ACTION_CATEGORIES.CUSTOMER;
                } else if (action.startsWith('온보딩_') || action.startsWith('첫고객_') || action.startsWith('기능_첫사용')) {
                    category = ACTION_CATEGORIES.ONBOARDING;
                } else if (action.startsWith('리포트_') || action.startsWith('CSV_')) {
                    category = ACTION_CATEGORIES.REPORT;
                } else if (action.startsWith('세션_')) {
                    category = ACTION_CATEGORIES.SESSION;
                } else {
                    category = ACTION_CATEGORIES.FEATURE;
                }
            }

            const deviceId = generateDeviceId();
            const sessionId = getOrCreateSessionId();

            const logData = {
                deviceId: deviceId,
                action: action,
                category: category,
                // 고객명·연락처·상담내용은 로컬에만 남긴다.
                details: Object.fromEntries(
                    Object.entries(details || {}).filter(([key, value]) =>
                        ['targetStage', 'stage', 'feature', 'count', 'result'].includes(key) &&
                        (typeof value === 'string' || typeof value === 'number')
                    )
                ),
                timestamp: getKoreanTimeISO(),
                sessionId: sessionId,
                userAgent: navigator.userAgent
            };

            const docRef = await addDoc(collection(db, 'action_logs'), logData);
            console.log('로그 전송 성공, 문서 ID:', docRef.id);
        } catch (error) {
            console.error('액션 로그 전송 실패:', error);
            // 로그 실패해도 기능은 계속 동작
        }
    }, []);

    return { sendActionLog };
};
